// MIDAS V3 — Attached chat column (MODEL-PICKER Feature 2, spec §6)
//
// The SAME chat window element as the primary chat, composed for an attached
// thread: slim header (thread title + ✕ detach, upper-right) + machine-pill
// header + ChatMessageListV3 + ChatInputAreaV3 — the list/composer are the
// SAME components, parameterised (messagesOverride / column props) rather
// than duplicated (design hard constraint: a column looks and behaves
// exactly like today's chat window).
//
// Per-column independence rides the existing MIDAS-SCOPESTREAM buffers:
// streaming chrome reads state.chatBot.threadBuffers[col.threadId] via the
// list's existing threadId prop. Message ARRAYS live in midasAttachSlice.
//
// The subtree is wrapped in AttachColumnContext so follow-up chips,
// regenerate, and artifact opens inside this column target THIS thread.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useMemo } from "react";

import { useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { Machine } from "../../../../machinery/models/models";
import { AttachedColumn } from "../../../midasAttachSlice";
import ChatInputAreaV3 from "../ChatInputAreaV3";
import ChatMessageListV3 from "../ChatMessageListV3";
import MachinePillV3 from "../MachinePillV3";
import { AttachColumnContext } from "./AttachColumnContext";

export interface AttachedChatColumnV3Props {
  column: AttachedColumn;
  /** Ask the strip to animate-collapse this column, then dispatch detach. */
  onRequestDetach: (threadId: string) => void;
}

const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

const AttachedChatColumnV3: React.FC<AttachedChatColumnV3Props> = ({
  column,
  onRequestDetach,
}) => {
  const { translate } = useTranslation("chatBot");
  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);

  // Resolve the column's machine display data (name + serial) from the
  // machinery slice — same "brand model" convention the rail uses.
  const machineInfo = useMemo(() => {
    if (!column.machineId) return null;
    const m = flattenMachines(machinesMap).find(
      (x) => x.id === column.machineId
    );
    if (!m) return null;
    const brand = (m as any).customBrandName || m.manufacturer?.name || "";
    const name = brand ? `${brand} ${m.model || ""}`.trim() : m.model || "";
    return {
      name: name || m.model || "",
      serialNumber: (m as any).serialNumber || undefined,
    };
  }, [machinesMap, column.machineId]);

  const contextValue = useMemo(
    () => ({ threadId: column.threadId, machineId: column.machineId }),
    [column.threadId, column.machineId]
  );

  const detachAria = translate("midas.v3.attach.detachAria", {
    defaultValue: "Detach this chat",
  }) as string;
  const detachTitle = translate("midas.v3.railDetach", {
    defaultValue: "Detach chat",
  }) as string;
  const loadFailed = translate("midas.v3.attach.loadFailed", {
    defaultValue: "Couldn't load this conversation",
  }) as string;
  const loadingLabel = translate("midas.v3.attach.loading", {
    defaultValue: "Loading conversation…",
  }) as string;

  return (
    <AttachColumnContext.Provider value={contextValue}>
      {/* Slim column header — 34px, bg-soft, title + ✕ (design handoff). */}
      <div
        style={{
          height: 34,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 8px 0 14px",
          background: "var(--bg-soft)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <span
          title={column.title}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            fontWeight: 500,
            color: "var(--ink-2)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: "var(--font-sans)",
          }}
        >
          {column.title}
        </span>
        <button
          type="button"
          aria-label={detachAria}
          title={detachTitle}
          onClick={() => onRequestDetach(column.threadId)}
          style={{
            width: 22,
            height: 22,
            borderRadius: 5,
            border: "none",
            background: "transparent",
            color: "var(--muted)",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            fontFamily: "var(--font-sans)",
            transition: "background 0.12s ease, color 0.12s ease",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--card-2)";
            e.currentTarget.style.color = "var(--ink)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--muted)";
          }}
        >
          ✕
        </button>
      </div>

      {/* Machine-context pill header — same chrome as the primary chat. */}
      <div
        style={{
          padding: "12px 24px",
          borderBottom: "1px solid var(--border)",
          background: "var(--bg)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <MachinePillV3
          platform={!column.machineId}
          machineOverride={
            column.machineId
              ? {
                  name: machineInfo?.name || "",
                  serialNumber: machineInfo?.serialNumber,
                }
              : undefined
          }
        />
      </div>

      {/* Body: loading / error / the SAME message list as the primary. */}
      {column.loading ? (
        <div
          role="status"
          style={{
            flex: 1,
            display: "grid",
            placeItems: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.06em",
            color: "var(--faint)",
            textTransform: "uppercase",
          }}
        >
          {loadingLabel}
        </div>
      ) : column.error ? (
        <div
          role="alert"
          style={{
            flex: 1,
            display: "grid",
            placeItems: "center",
            padding: 24,
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            color: "var(--red)",
            textAlign: "center",
          }}
        >
          {loadFailed}
        </div>
      ) : (
        <ChatMessageListV3
          threadId={column.threadId}
          messagesOverride={column.messages}
          compact
          suppressWelcome
        />
      )}

      {/* The SAME composer, targeting THIS thread (distinct thread_id on
          every send — spec §6 hard requirement) with its own model picker. */}
      <ChatInputAreaV3
        column={{
          threadId: column.threadId,
          machineId: column.machineId,
          machineName: machineInfo?.name,
        }}
      />
    </AttachColumnContext.Provider>
  );
};

export default AttachedChatColumnV3;
